<template>
  <div class="messaging-layout">
    <!-- Sidebar -->
    <Sidebar :isAgent="isAgent" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="messaging-container">
          <div class="messaging-layout-inner">
            <!-- Conversations List -->
            <div class="conversations-container" :class="{ 'mobile-hidden': selectedConversation && isMobile }">

              <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading conversations...</p>
              </div>

              <div v-else-if="conversations.length === 0" class="empty-state">
                <div class="empty-icon">💬</div>
                <h3>No conversations yet</h3>
                <p>Start a conversation to see it here</p>
              </div>

              <div v-else class="conversations-list">
                <div
                  v-for="conversation in conversations"
                  :key="conversation.conversation_key"
                  class="conversation-item"
                  :class="{
                    'has-unread': conversation.unread_count > 0,
                    'selected': selectedConversation && selectedConversation.other_id === conversation.other_id
                  }"
                  @click="openConversation(conversation)"
                >
                  <div class="conversation-avatar">
                    <div class="avatar-circle" :class="getAvatarClass(conversation.other_name)">
                      {{ getInitials(conversation.other_name) }}
                    </div>
                    <div v-if="isOnline(conversation.other_id)" class="online-indicator"></div>
                  </div>

                  <div class="conversation-content">
                    <div class="conversation-header">
                      <h4 class="contact-name">{{ conversation.other_name }}</h4>
                      <div class="conversation-meta">
                        <span v-if="conversation.unread_count > 0" class="unread-badge">
                          {{ conversation.unread_count }}
                        </span>
                        <span class="timestamp">{{ formatTime(conversation.last_message_time) }}</span>
                      </div>
                    </div>

                    <div class="last-message">
                      <span v-if="conversation.is_sent_by_me" class="message-indicator">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        You:
                      </span>
                      <span class="message-text">{{ conversation.last_message || 'No messages yet' }}</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <!-- Conversation View -->
            <div v-if="selectedConversation" class="conversation-view">
              <div class="conversation-header">
                <div class="conversation-info">
                  <div class="avatar-circle" :class="getAvatarClass(selectedConversation.other_name)">
                    {{ getInitials(selectedConversation.other_name) }}
                  </div>
                  <div class="contact-details">
                    <h3>{{ selectedConversation.other_name }}</h3>
                    <span class="contact-status" :class="{ online: isOnline(selectedConversation.other_id) }">
                      {{ isOnline(selectedConversation.other_id) ? 'Online' : 'Offline' }}
                    </span>
                  </div>
                </div>
                <button @click="closeConversation" class="close-btn" :title="isMobile ? 'Back to conversations' : 'Close conversation'">
                  <svg v-if="isMobile" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>

              <div class="messages-container" ref="messagesContainer">
                <div class="messages-list">
                  <div v-if="currentConversationMessages.length === 0" class="no-messages">
                    <div class="no-messages-icon">💬</div>
                    <p>No messages yet. Start the conversation!</p>
                  </div>

                  <div
                    v-for="message in sortedMessages"
                    :key="message.id"
                    class="message"
                    :class="{ 'sent': message.is_sent_by_me, 'received': !message.is_sent_by_me }"
                  >
                    <div class="message-content">
                      <div class="message-text">{{ message.message }}</div>
                      <div class="message-meta">
                        <span class="message-time">{{ formatMessageTime(message.time) }}</span>
                        <span v-if="message.is_sent_by_me && !message.is_read" class="message-status unread">Sent</span>
                        <span v-else-if="message.is_sent_by_me && message.is_read" class="message-status read">Read</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="message-input-container">
                <form @submit.prevent="sendMessage" class="message-form">
                  <div class="input-wrapper">
                    <textarea
                      v-model="newMessage"
                      placeholder="Type your message..."
                      class="message-input"
                      rows="1"
                      @keydown="handleKeyDown"
                      ref="messageInput"
                    ></textarea>
                    <button
                      type="submit"
                      class="send-btn"
                      :disabled="!newMessage.trim() || sendingMessage"
                      title="Send message"
                    >
                      <svg v-if="sendingMessage" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="sending-icon">
                        <path d="M12 4V2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 16.17l7.59-7.59L19 10l-9 9z"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <!-- No Conversation Selected -->
            <div v-else class="no-conversation-selected">
              <div class="placeholder-content">
                <div class="placeholder-icon">💬</div>
                <h3>Select a conversation</h3>
                <p>Choose a conversation from the list to start messaging</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { DashboardService } from '../services/dashboard'

export default {
  name: 'MessagingView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      isAgent: !!localStorage.getItem('agent_token'),
      conversations: [],
      loading: false,
      onlineUsers: [1, 3, 9], // Mock online status - in real app this would come from API
      refreshInterval: null,
      selectedConversation: null,
      currentConversationMessages: [],
      loadingMessages: false,
      newMessage: '',
      sendingMessage: false,
      conversationRefreshInterval: null,
      isMobile: false
    }
  },
  computed: {
    sortedMessages () {
      // Sort messages by time (oldest first, newest last) for proper chat order
      return [...this.currentConversationMessages].sort((a, b) => {
        const timeA = new Date(a.time).getTime()
        const timeB = new Date(b.time).getTime()
        return timeA - timeB
      })
    }
  },
  async mounted () {
    await this.loadConversations()
    this.startAutoRefresh()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount () {
    this.stopAutoRefresh()
    this.stopConversationRefresh()
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    async loadConversations (isAutoRefresh = false) {
      try {
        if (!isAutoRefresh) {
          this.loading = true
        }

        const result = await DashboardService.getConversations()

        if (result.success) {
          this.conversations = result.conversations
        } else {
          console.error('Failed to load conversations:', result.error)
          if (!isAutoRefresh) {
            alert('Failed to load conversations: ' + (result.error || 'Unknown error'))
          }
        }
      } catch (error) {
        console.error('Error loading conversations:', error)
        if (!isAutoRefresh) {
          alert('Error loading conversations: ' + error.message)
        }
      } finally {
        if (!isAutoRefresh) {
          this.loading = false
        }
      }
    },

    startAutoRefresh () {
      // Refresh every 30 seconds
      this.refreshInterval = setInterval(async () => {
        await this.loadConversations(true)
      }, 30000)
    },

    stopAutoRefresh () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },

    getInitials (name) {
      return name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },

    getAvatarClass (name) {
      // Generate consistent color based on name
      const colors = ['avatar-blue', 'avatar-green', 'avatar-purple', 'avatar-orange', 'avatar-pink']
      const index = name.length % colors.length
      return colors[index]
    },

    isOnline (userId) {
      return this.onlineUsers.includes(userId)
    },

    formatTime (timeString) {
      if (!timeString) return ''

      try {
        const date = new Date(timeString)
        const now = new Date()
        const diffInHours = Math.abs(now - date) / 36e5

        if (diffInHours < 1) {
          const minutes = Math.floor(diffInHours * 60)
          return minutes <= 1 ? 'Just now' : `${minutes}m ago`
        } else if (diffInHours < 24) {
          return `${Math.floor(diffInHours)}h ago`
        } else if (diffInHours < 168) { // 7 days
          const days = Math.floor(diffInHours / 24)
          return `${days}d ago`
        } else {
          return date.toLocaleDateString()
        }
      } catch (error) {
        return timeString
      }
    },

    async openConversation (conversation) {
      this.selectedConversation = conversation
      await this.loadConversationMessages(conversation.other_id)
      this.startConversationRefresh()
    },

    closeConversation () {
      this.selectedConversation = null
      this.currentConversationMessages = []
      this.newMessage = ''
      this.stopConversationRefresh()
    },

    async loadConversationMessages (agentId, isBackgroundRefresh = false) {
      try {
        // Only show loading state on initial load, not background refreshes
        if (!isBackgroundRefresh) {
          this.loadingMessages = true
        }

        const result = await DashboardService.getConversation(agentId)

        if (result.success) {
          const wasAtBottom = this.isScrolledToBottom()
          this.currentConversationMessages = result.conversation.messages || []

          this.$nextTick(() => {
            // Only auto-scroll if user was already at bottom or if it's initial load
            if (!isBackgroundRefresh || wasAtBottom) {
              this.scrollToBottom()
            }
          })
        } else {
          console.error('Failed to load conversation messages:', result.error)
        }
      } catch (error) {
        console.error('Error loading conversation messages:', error)
      } finally {
        if (!isBackgroundRefresh) {
          this.loadingMessages = false
        }
      }
    },

    isScrolledToBottom () {
      if (!this.$refs.messagesContainer) return true
      const container = this.$refs.messagesContainer
      const threshold = 50 // pixels from bottom
      return container.scrollTop + container.clientHeight >= container.scrollHeight - threshold
    },

    async sendMessage () {
      if (!this.newMessage.trim() || this.sendingMessage || !this.selectedConversation) {
        return
      }

      try {
        this.sendingMessage = true
        const result = await DashboardService.sendMessage(
          this.selectedConversation.other_id,
          this.newMessage.trim()
        )

        if (result.success) {
          // Add the new message to the current conversation
          this.currentConversationMessages.push(result.message)
          this.newMessage = ''

          // Refresh conversations list to update last message and unread counts
          await this.loadConversations(true)

          this.$nextTick(() => {
            this.scrollToBottom()
            if (this.$refs.messageInput) {
              this.$refs.messageInput.focus()
            }
          })
        } else {
          alert('Failed to send message: ' + (result.error || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error sending message:', error)
        alert('Error sending message: ' + error.message)
      } finally {
        this.sendingMessage = false
      }
    },

    handleKeyDown (event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendMessage()
      }
    },

    scrollToBottom () {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },

    startConversationRefresh () {
      this.stopConversationRefresh()
      // Refresh conversation messages every 10 seconds when a conversation is open
      this.conversationRefreshInterval = setInterval(async () => {
        if (this.selectedConversation) {
          await this.loadConversationMessages(this.selectedConversation.other_id, true) // true = background refresh
        }
      }, 10000)
    },

    stopConversationRefresh () {
      if (this.conversationRefreshInterval) {
        clearInterval(this.conversationRefreshInterval)
        this.conversationRefreshInterval = null
      }
    },

    formatMessageTime (timeString) {
      if (!timeString) return ''

      try {
        // Handle the format "Aug 19, 2025 7:56 PM"
        const date = new Date(timeString)
        const now = new Date()
        const diffInHours = Math.abs(now - date) / 36e5

        if (diffInHours < 1) {
          const minutes = Math.floor(diffInHours * 60)
          return minutes <= 1 ? 'Just now' : `${minutes}m ago`
        } else if (diffInHours < 24) {
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        } else {
          return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
        }
      } catch (error) {
        return timeString
      }
    },

    checkMobile () {
      this.isMobile = window.innerWidth <= 768
    }
  }
}
</script>

<style scoped>
.messaging-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 32px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.title-icon {
  color: #3b82f6;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

.header-stats {
  display: flex;
  gap: 32px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.stat-label {
  font-size: 12px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.conversations-list {
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

.conversation-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-secondary);
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.conversation-item:hover {
  background: var(--bg-tertiary);
  transition: background-color 0.2s ease;
}

.conversation-item.has-unread {
  background: rgba(59, 130, 246, 0.05);
  border-left: 4px solid #3b82f6;
}

.conversation-item.has-unread:hover {
  background: rgba(59, 130, 246, 0.1);
}

.conversation-item.has-unread .contact-name {
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.conversation-item.has-unread .message-text {
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.conversation-avatar {
  position: relative;
  flex-shrink: 0;
}

.avatar-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  color: white;
}

.avatar-blue { background: linear-gradient(135deg, #3b82f6, #1d4ed8); }
.avatar-green { background: linear-gradient(135deg, #10b981, #059669); }
.avatar-purple { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
.avatar-orange { background: linear-gradient(135deg, #f59e0b, #d97706); }
.avatar-pink { background: linear-gradient(135deg, #ec4899, #db2777); }

.online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  background: #10b981;
  border: 2px solid var(--bg-primary);
  border-radius: 50%;
  transition: border-color 0.3s ease;
}

.conversation-content {
  flex: 1;
  min-width: 0;
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  truncate: true;
  transition: color 0.3s ease;
}

.conversation-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.unread-badge {
  background: #3b82f6;
  color: white;
  font-size: 13px;
  font-weight: 800;
  padding: 5px 9px;
  border-radius: 16px;
  min-width: 24px;
  text-align: center;
  line-height: 1;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.timestamp {
  font-size: 12px;
  color: var(--text-tertiary);
  white-space: nowrap;
  transition: color 0.3s ease;
}

.last-message {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.message-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #3b82f6;
  flex-shrink: 0;
}

.message-text {
  truncate: true;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  transition: border-color 0.3s ease;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.empty-state p {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
    padding: 20px 16px;
  }

  .header-stats {
    justify-content: center;
    gap: 24px;
  }

  .conversations-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .conversation-item {
    padding: 12px 16px;
  }

  .avatar-circle {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }

  .contact-name {
    font-size: 15px;
  }

  .conversation-actions {
    opacity: 1;
  }
}

/* Messaging Container */
.messaging-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* Two-panel layout */
.messaging-layout-inner {
  display: flex;
  flex: 1;
  height: 100%;
}

.conversations-container {
  flex: 0 0 400px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.conversation-item.selected {
  background: rgba(59, 130, 246, 0.05);
  border-left: 4px solid #3b82f6;
  border-color: #3b82f6;
}

/* Conversation View */
.conversation-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  overflow: hidden;
  transition: background-color 0.3s ease;
}

.conversation-view .conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.conversation-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.contact-details h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px;
  transition: color 0.3s ease;
}

.contact-status {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
  transition: color 0.3s ease;
}

.contact-status.online {
  color: #10b981;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
  transform: translateY(-1px);
}

/* Messages */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 16px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.no-messages-icon {
  font-size: 48px;
  opacity: 0.5;
}

.message {
  display: flex;
  margin-bottom: 8px;
}

.message.sent {
  justify-content: flex-end;
}

.message.received {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message.sent .message-content {
  align-items: flex-end;
}

.message.received .message-content {
  align-items: flex-start;
}

.message-text {
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;
  line-height: 1.4;
}

.message.sent .message-text {
  background: #3b82f6;
  color: white;
  border-bottom-right-radius: 4px;
}

.message.received .message-text {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-bottom-left-radius: 4px;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--text-tertiary);
  padding: 0 4px;
  transition: color 0.3s ease;
}

.message-time {
  font-weight: 500;
}

.message-status.unread {
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.message-status.read {
  color: #10b981;
}

/* Message Input */
.message-input-container {
  padding: 16px 24px 20px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.message-form {
  width: 100%;
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: var(--bg-primary);
  border-radius: 24px;
  border: 1px solid var(--border-primary);
  transition: all 0.2s;
  min-height: 48px;
}

.input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.message-input {
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  line-height: 1.4;
  color: var(--text-primary);
  background: transparent;
  max-height: 120px;
  min-height: 24px;
  padding: 8px 0;
  font-family: inherit;
  vertical-align: middle;
  overflow: hidden;
  transition: color 0.3s ease;
}

.message-input::placeholder {
  color: var(--text-tertiary);
  line-height: 1.4;
  transition: color 0.3s ease;
}

.send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: scale(1.05);
}

.send-btn:disabled {
  background: var(--border-primary);
  color: var(--text-tertiary);
  cursor: not-allowed;
  transform: none;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.sending-icon {
  animation: spin 1s linear infinite;
}

/* No Conversation Selected */
.no-conversation-selected {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  transition: background-color 0.3s ease;
}

.placeholder-content {
  text-align: center;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.placeholder-icon {
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 16px;
}

.placeholder-content h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
  transition: color 0.3s ease;
}

.placeholder-content p {
  font-size: 16px;
  margin: 0;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  body {
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
  }

  .main-content {
    margin-left: 0;
    padding-top: 64px;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .page-content {
    padding: 8px;
    width: 100%;
    box-sizing: border-box;
  }

  .messaging-container {
    height: calc(100vh - 80px);
    border-radius: 8px;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .messaging-layout-inner {
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  /* On mobile, show either conversations OR conversation view, not both */
  .conversations-container {
    flex: 1;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  /* Hide conversations list when a conversation is selected on mobile */
  .conversations-container.mobile-hidden {
    display: none;
  }

  .conversation-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .no-conversation-selected {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Mobile conversation item styling */
  .conversation-item {
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    min-height: 60px; /* Ensure good touch target */
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  .conversation-item:active {
    background: #f0f9ff;
    transition: background 0.1s;
  }

  .conversation-content {
    flex: 1;
    min-width: 0; /* Allow text to truncate */
    overflow: hidden;
  }

  .contact-name {
    font-size: 15px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .last-message {
    width: 100%;
    overflow: hidden;
  }

  .message-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .avatar-circle {
    width: 40px;
    height: 40px;
    font-size: 14px;
    flex-shrink: 0;
  }

  /* Mobile conversation view header */
  .conversation-view .conversation-header {
    padding: 16px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    position: relative;
  }

  .conversation-info {
    gap: 12px;
  }

  .contact-details h3 {
    font-size: 16px;
  }

  .contact-status {
    font-size: 11px;
  }

  /* Add back button for mobile */
  .close-btn {
    width: 32px;
    height: 32px;
  }

  /* Mobile messages container */
  .messages-container {
    flex: 1;
    padding: 8px 12px;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    box-sizing: border-box;
  }

  .messages-list {
    gap: 8px;
    width: 100%;
    max-width: 100%;
  }

  .message {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .message-content {
    max-width: 85%;
    box-sizing: border-box;
    overflow: hidden;
  }

  .message-text {
    padding: 10px 14px;
    font-size: 14px;
    border-radius: 16px;
  }

  .message.sent .message-text {
    border-bottom-right-radius: 4px;
  }

  .message.received .message-text {
    border-bottom-left-radius: 4px;
  }

  .message-meta {
    font-size: 10px;
    gap: 6px;
  }

  /* Mobile message input */
  .message-input-container {
    padding: 8px 12px 12px;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    /* Safe area for devices with home indicator */
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    width: 100%;
    box-sizing: border-box;
  }

  .input-wrapper {
    padding: 6px 10px;
    min-height: 44px; /* Minimum touch target size */
    border-radius: 22px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
    box-sizing: border-box;
  }

  .message-input {
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 22px;
    padding: 4px 0;
    line-height: 1.4;
    width: 100%;
    box-sizing: border-box;
  }

  .send-btn {
    width: 32px;
    height: 32px;
  }

  /* Mobile loading and empty states */
  .loading-state,
  .empty-state {
    padding: 32px 16px;
  }

  .loading-state p {
    font-size: 14px;
  }

  .empty-state h3 {
    font-size: 18px;
  }

  .empty-state p {
    font-size: 14px;
  }

  /* No messages state for mobile */
  .no-messages {
    height: 150px;
    gap: 12px;
  }

  .no-messages-icon {
    font-size: 36px;
  }

  .no-messages p {
    font-size: 14px;
  }

  /* Conversations list mobile fixes */
  .conversations-list {
    max-height: none;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
  }

  /* Loading and empty states */
  .loading-state,
  .empty-state {
    width: 100%;
    box-sizing: border-box;
  }
}
</style>
